import styled from '@emotion/styled'
import React, { ReactNode, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import InfoBlock from '../../components/InfoBlock'

import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

import { Theme, useTheme } from '@emotion/react'
import { formatBigMoney, formatInteger, formatNumber, formatPercentage } from '../../../packages/utils/numberFormatters'
import * as sharesOrUnits from '../../../utils/issues/sharesOrUnits'
import { getLevelName, calcIssueOutcome } from '../utils'
import { EnrichedIssueEntity } from 'types/IssueEntity'

interface IPalette {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
}

const ChartWrapper = styled.div`
    height: 200px;
    position: relative;
    margin: 20px;
`

const DoughnutInner = styled.div`
    width: 100%;
    position: absolute;
    top: 50%;
    left: 0;
    margin-top: -22px;
    text-align: center;
    z-index: 0;
`

function EmptyBox({ color, side = null }) {
    const sideLength = side || 30
    return <div style={{ width: sideLength, height: sideLength, backgroundColor: color }}></div>
}

export const IssueOutcomeOverview = ({ issue }: { issue: EnrichedIssueEntity }) => {
    const [showDetailedGuarantorView, setShowDetailedGuarantorView] = useState(false)
    const { i18n } = useTranslation()
    const locale = i18n.language
    const theme = useTheme() as Theme & IPalette

    let content: ReactNode | null = null

    const colors = {
        pctSubscribedWithRights: theme.palette.success.main,
        pctSubscribedWithoutRights: theme.palette.success.light,
        pctSubscribedGuarantors: theme.palette.warning.main,
        pctUnsubscribed: theme.palette.grey[200],
    }

    if (issue) {
        const outcome = calcIssueOutcome(issue)

        const data = {
            labels: ['Tecknat med företräde', 'Tecknat utan företräde', 'Tecknat av garanter', 'Ej tecknat'],
            datasets: [
                {
                    data: [
                        outcome.pctSubscribedWithRights,
                        outcome.pctSubscribedWithoutRights,
                        outcome.pctSubscribedGuarantors,
                        outcome.pctUnsubscribed,
                    ],
                    backgroundColor: [
                        colors.pctSubscribedWithRights,
                        colors.pctSubscribedWithoutRights,
                        colors.pctSubscribedGuarantors,
                        colors.pctUnsubscribed,
                    ],
                    borderWidth: 1,
                    borderColor: theme.palette.grey[400],
                },
            ],
        }
        const options = {
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        label: (item) => formatPercentage(item.parsed, { locale, decimals: 1 }),
                    },
                },
            },
            cutout: '70%',
        }
        const formatSubVolume = (subVolume /* , approx */) => {
            const number = formatInteger(subVolume, { locale })
            return number
            // return `${approx ? 'ca ' : ''}${number}` // don't know if we should show "ca" or not
        }

        const maxFeeLevel = Math.max(...issue.fees.map((level) => level.feeLevel))

        const bodyRows: ReactNode[] = []
        if (outcome.pctSubscribedWithRights > 0 || outcome.unitSubWithRights > 0) {
            bodyRows.push(
                <TableRow key={'pctSubscribedWithRights'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedWithRights} />
                    </TableCell>
                    <TableCell>Med företräde</TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedWithRights, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubWithRights)}</TableCell>
                    <TableCell>{formatBigMoney(issue.unitSubPrice * outcome.unitSubWithRights, { locale })}</TableCell>
                </TableRow>,
            )
        }
        if (outcome.pctSubscribedWithoutRights > 0 || outcome.unitSubWithoutRights > 0) {
            bodyRows.push(
                <TableRow key={'pctSubscribedWithoutRights'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedWithoutRights} />
                    </TableCell>
                    <TableCell>Utan företräde</TableCell>
                    <TableCell>
                        {formatPercentage(outcome.pctSubscribedWithoutRights, { locale, decimals: 0 })}
                    </TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubWithoutRights)}</TableCell>
                    <TableCell>
                        {formatBigMoney(issue.unitSubPrice * outcome.unitSubWithoutRights, { locale })}
                    </TableCell>
                </TableRow>,
            )
        }
        if (outcome.pctSubscribedGuarantors > 0 || outcome.unitSubGuarantor > 0) {
            bodyRows.push(
                <TableRow
                    key={'pctSubscribedGuarantors'}
                    sx={showDetailedGuarantorView ? { '.MuiTableCell-root': { borderBottom: 'none' } } : null}
                >
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedGuarantors} />
                    </TableCell>
                    <TableCell>
                        <Stack direction='column'>
                            <Typography>Av garanter</Typography>
                            {outcome.detailedGuarantorData.length > 1 ? (
                                <Button
                                    onClick={() => setShowDetailedGuarantorView(!showDetailedGuarantorView)}
                                    sx={{ padding: 0, justifyContent: 'left', textAlign: 'left' }}
                                >
                                    {showDetailedGuarantorView ? 'Dölj detaljerat' : 'Visa detaljerat'}
                                </Button>
                            ) : null}
                        </Stack>
                    </TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedGuarantors, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubGuarantor)}</TableCell>
                    <TableCell>{formatBigMoney(issue.unitSubPrice * outcome.unitSubGuarantor, { locale })}</TableCell>
                </TableRow>,
            )

            if (showDetailedGuarantorView && outcome.detailedGuarantorData.length > 1) {
                bodyRows.push(
                    ...outcome.detailedGuarantorData.map((level, index) => (
                        <TableRow
                            key={level.feeLevel}
                            sx={
                                outcome.detailedGuarantorData.length - 1 !== index
                                    ? { td: { borderBottom: 'none', paddingY: 0 } }
                                    : { td: { paddingTop: 0 } }
                            }
                        >
                            <TableCell />
                            <TableCell>{getLevelName(level.feeLevel, maxFeeLevel)}</TableCell>
                            <TableCell>{formatPercentage(level.pctOfIssue * 100, { locale, decimals: 0 })}</TableCell>
                            <TableCell>{formatSubVolume(level.numberOfUnits)}</TableCell>
                            <TableCell>
                                {formatBigMoney(level.amount, { locale })} (
                                {formatPercentage(level.pctOfGuarantSub * 100, { locale, decimals: 0 })})
                            </TableCell>
                        </TableRow>
                    )),
                )
            }
        } else if (!issue.noGuarantors) {
            bodyRows.push(
                <TableRow key={'pctSubscribedGuarantors'}>
                    <TableCell>
                        <EmptyBox color={colors.pctSubscribedGuarantors} />
                    </TableCell>
                    <TableCell>Av garanter</TableCell>
                    <TableCell>{formatPercentage(outcome.pctSubscribedGuarantors, { locale, decimals: 0 })}</TableCell>
                    <TableCell>{formatSubVolume(outcome.unitSubGuarantor)}</TableCell>
                    <TableCell>-</TableCell>
                </TableRow>,
            )
        }

        if (issue.overAllotmentSize > 0 || issue.overAllotmentSizeSecondary > 0) {
            const outcomeNotReady =
                issue.overAllotmentAllocation === null && issue.overAllotmentAllocationSecondary === null
            bodyRows.push(
                <TableRow key={'overAllotment'}>
                    <TableCell />
                    <TableCell>Övertilldelning</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>{outcomeNotReady ? '-' : formatSubVolume(outcome.overAllotment)}</TableCell>
                    <TableCell>
                        {outcome.overAllotment === 0 || outcomeNotReady
                            ? '-'
                            : formatBigMoney(issue.unitSubPrice * outcome.overAllotment, { locale })}
                    </TableCell>
                </TableRow>,
            )
        }

        content = (
            <>
                <ChartWrapper>
                    <DoughnutInner>
                        <Typography variant='h4'>
                            {formatPercentage(100 - outcome.pctUnsubscribed, { locale, decimals: 0 })}
                        </Typography>
                        <Typography variant='caption'>teckningsgrad</Typography>
                    </DoughnutInner>
                    <Doughnut data={data} options={options} />
                </ChartWrapper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>Tecknat</TableCell>
                            <TableCell>Procent</TableCell>
                            <TableCell>Antal {sharesOrUnits.isUnit(issue) ? 'units' : 'aktier'}</TableCell>
                            <TableCell>Belopp</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {bodyRows}
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell></TableCell>
                            <TableCell>Summa</TableCell>
                            <TableCell>
                                {formatPercentage(
                                    outcome.pctSubscribedWithRights +
                                        outcome.pctSubscribedWithoutRights +
                                        outcome.pctSubscribedGuarantors,
                                    { decimals: 0, locale },
                                )}
                            </TableCell>
                            <TableCell>
                                {formatInteger(
                                    outcome.unitSubWithRights +
                                        outcome.unitSubWithoutRights +
                                        outcome.unitSubGuarantor +
                                        outcome.overAllotment,
                                    { locale },
                                )}
                            </TableCell>
                            <TableCell>
                                {formatBigMoney(
                                    issue.unitSubPrice *
                                        (outcome.unitSubWithRights +
                                            outcome.unitSubWithoutRights +
                                            outcome.unitSubGuarantor +
                                            outcome.overAllotment),
                                    { locale },
                                )}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                {issue.unitSubTotal && (
                    <>
                        <Typography variant='body1' gutterBottom>
                            Totalt tecknades{' '}
                            {sharesOrUnits.quantityLabel(issue, issue.unitSubTotal + outcome.overAllotment, locale)} för{' '}
                            {formatBigMoney((issue.unitSubTotal + outcome.overAllotment) * issue.unitSubPrice, {
                                locale,
                                currencySymbol: issue.currency,
                                decimals: 0,
                                tooltip: true,
                            })}{' '}
                        </Typography>
                        <Typography variant='body1' gutterBottom>
                            Teckningsgraden uppgick till{' '}
                            {formatPercentage(100 * (issue.unitSubTotal / issue.totalOfferedUnits), {
                                locale,
                                decimals: 2,
                            })}{' '}
                            varav{' '}
                            {formatPercentage(outcome.pctSubscribedWithRights, { locale, decimals: 2 }) ||
                                formatPercentage(100 * (outcome.unitSubWithRights / outcome.unitSubTotal), {
                                    locale,
                                    decimals: 2,
                                })}{' '}
                            av erbjudandet tecknades med stöd av rätter och{' '}
                            {formatPercentage(
                                outcome.pctSubscribedWithoutRights ||
                                    outcome.unitSubWithoutRights / outcome.unitSubTotal,
                                { locale, decimals: 2 },
                            )}{' '}
                            utan stöd av rätter.
                        </Typography>

                        {outcome.pctSubscribedGuarantors > 0 && (
                            <Typography variant='body1' gutterBottom>
                                Garantiåtagare tecknade{' '}
                                {formatPercentage(outcome.pctSubscribedGuarantors, { locale, decimals: 0 })} av
                                erbjudandet, motsvarande{' '}
                                {formatPercentage(
                                    (100 * outcome.pctSubscribedGuarantors) /
                                        (outcome.pctSubscribedGuarantors +
                                            outcome.pctSubscribedWithRights +
                                            outcome.pctSubscribedWithoutRights),
                                    {
                                        locale,
                                        decimals: 0,
                                    },
                                )}{' '}
                                av emitterat antal aktier, eller{' '}
                                {formatPercentage(
                                    (outcome.pctSubscribedGuarantors / outcome.pctGuarantorCommitment) * 100,
                                    {
                                        locale,
                                        decimals: 0,
                                    },
                                )}{' '}
                                av det garanterade beloppet.
                            </Typography>
                        )}
                        {issue.overAllotmentSize > 0 ? (
                            <Typography variant='body1' gutterBottom>
                                Övertilldelningen om max{' '}
                                {sharesOrUnits.quantityLabel(issue, issue.overAllotmentSize, locale)} nyttjades{' '}
                                {issue.overAllotmentAllocation === 0
                                    ? 'inte'
                                    : `till ${sharesOrUnits.quantityLabel(
                                          issue,
                                          issue.overAllotmentAllocation,
                                          locale,
                                      )}`}
                                .
                            </Typography>
                        ) : null}
                        {issue.overAllotmentSizeSecondary > 0 ? (
                            <Typography variant='body1' gutterBottom>
                                Övertilldelningen om max{' '}
                                {formatNumber(issue.overAllotmentSizeSecondary, { locale, decimals: 0 })} befintliga
                                aktier (secondary) nyttjades{' '}
                                {issue.overAllotmentAllocationSecondary === 0
                                    ? 'inte'
                                    : `till ${sharesOrUnits.quantityLabel(
                                          issue,
                                          issue.overAllotmentAllocationSecondary,
                                          locale,
                                      )}`}
                                .
                            </Typography>
                        ) : null}
                        {issue.secondaryOffering > 0 ? (
                            <Typography variant='body1' gutterBottom>
                                Av de befintliga {sharesOrUnits.quantityLabel(issue, issue.secondaryOffering, locale)}{' '}
                                som erbjöds (secondary) nyttjades{' '}
                                {sharesOrUnits.quantityLabel(issue, issue.secondarySub, locale)}.
                            </Typography>
                        ) : null}
                    </>
                )}
            </>
        )
    }
    return (
        <InfoBlock title='Utfall' loadingElement={!content}>
            {content}
        </InfoBlock>
    )
}
